/**
 * pages/Login.jsx
 * Email/password + magic link login page
 */

import { useEffect, useState } from "react";
import { LogoLockup } from "../components/Logo";

const API = import.meta.env.VITE_API_URL || '';

export function Login({ onLogin, initialError = '', initialTab = 'password' }) {
  const [tab, setTab]       = useState(initialTab);
  const [email, setEmail]   = useState('');
  const [password, setPass] = useState('');
  const [loading, setLoad]  = useState(false);
  const [error, setError]   = useState(initialError);
  const [magicSent, setMagicSent] = useState(false);

  useEffect(() => {
    setTab(initialTab);
    setError(initialError);
  }, [initialError, initialTab]);

  const handlePassword = async e => {
    e.preventDefault();
    setLoad(true); setError('');
    try {
      const r = await fetch(`${API}/api/auth/login`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ email, password })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Login failed');
      onLogin(d.user, d.access_token, d.refresh_token);
    } catch(err) {
      setError(err instanceof TypeError ? 'Network error. Please try again.' : err.message);
    } finally { setLoad(false); }
  };

  const handleMagic = async e => {
    e.preventDefault();
    setLoad(true); setError('');
    try {
      const r = await fetch(`${API}/api/auth/magic-link`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ email })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not send login link');
      setMagicSent(true);
    } catch(err) {
      setError(err instanceof TypeError ? 'Network error. Please try again.' : (err.message || 'Something went wrong'));
    } finally { setLoad(false); }
  };

  const S = {
    wrap:    { minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg)', padding:20 },
    box:     { width:420, background:'var(--card)', border:'1px solid var(--b)', borderRadius:14, padding:'40px 40px 36px' },
    logoRow: { display:'flex', alignItems:'center', justifyContent:'center', marginBottom:32 },
    tabs:    { display:'flex', gap:4, background:'var(--card2)', borderRadius:8, padding:3, marginBottom:24 },
    tab:     active => ({ flex:1, padding:'8px 0', textAlign:'center', borderRadius:6, border:'none', background: active?'var(--or)':'transparent', color: active?'#fff':'var(--mu)', fontFamily:'var(--sans)', fontSize:12, fontWeight:600, cursor:'pointer', transition:'all 0.15s' }),
    label:   { fontFamily:'var(--mono)', fontSize:10, color:'var(--mu)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:6, display:'block' },
    field:   { marginBottom:16 },
    err:     { background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.25)', borderRadius:6, padding:'8px 12px', fontFamily:'var(--mono)', fontSize:11, color:'#f87171', marginBottom:16 },
    sent:    { textAlign:'center', padding:'20px 0', fontFamily:'var(--mono)', fontSize:12, color:'#4ade80', lineHeight:1.7 },
  };

  return (
    <div style={S.wrap}>
      <div style={S.box}>
        <div style={S.logoRow}>
          <LogoLockup size={40} gap={12} />
        </div>

        <div style={S.tabs}>
          <button style={S.tab(tab==='password')} onClick={()=>{ setTab('password'); setError(''); }}>Password</button>
          <button style={S.tab(tab==='magic')}    onClick={()=>{ setTab('magic');    setError(''); }}>Magic link</button>
        </div>

        {error && <div style={S.err}>{error}</div>}

        {tab==='password' && (
          <form onSubmit={handlePassword}>
            <div style={S.field}>
              <label style={S.label}>Email</label>
              <input className="input" type="email" required placeholder="nick@hyring.ai" value={email} onChange={e=>setEmail(e.target.value)}/>
            </div>
            <div style={S.field}>
              <label style={S.label}>Password</label>
              <input className="input" type="password" required placeholder="••••••••" value={password} onChange={e=>setPass(e.target.value)}/>
            </div>
            <button className="btn primary" type="submit" disabled={loading} style={{width:'100%',padding:'11px'}}>
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        )}

        {tab==='magic' && !magicSent && (
          <form onSubmit={handleMagic}>
            <div style={S.field}>
              <label style={S.label}>Email address</label>
              <input className="input" type="email" required placeholder="nick@hyring.ai" value={email} onChange={e=>setEmail(e.target.value)}/>
            </div>
            <button className="btn primary" type="submit" disabled={loading} style={{width:'100%',padding:'11px'}}>
              {loading ? 'Sending…' : 'Send login link'}
            </button>
          </form>
        )}

        {tab==='magic' && magicSent && (
          <div style={S.sent}>
            <div style={{fontSize:28, marginBottom:12}}>✉</div>
            Check your inbox.<br/>
            Click the link to sign in.<br/>
            <span style={{color:'var(--mu)'}}>Link expires in 15 minutes.</span>
          </div>
        )}
      </div>
    </div>
  );
}
